import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Search, ShoppingCart, Package, Loader2, Store, AlertCircle } from 'lucide-react';
import ProductImage from '../components/ProductImage';

const formatMoney = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount);
};

export default function StorePage() {
  const { domain } = useParams();
  const [storeInfo, setStoreInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStoreInfo();
  }, [domain]);

  useEffect(() => {
    if (storeInfo) {
      fetchProducts();
    }
  }, [storeInfo, activeCategory]);

  const fetchStoreInfo = async () => {
    try {
      const response = await api.get(`/store/${domain}`);
      setStoreInfo(response.data);
      // Fetch categories
      const catRes = await api.get('/categories');
      setCategories(catRes.data);
    } catch (error) {
      setError('Store not found or inactive');
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/store/${domain}/products`, {
        params: { category_id: activeCategory }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProducts = keyword 
    ? products.filter(p => p.title.toLowerCase().includes(keyword.toLowerCase()))
    : products;

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <Card className="bg-[#0A0A0A] border-red-500/30 max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Store Not Found</h1>
            <p className="text-zinc-400 mb-4">{error}</p>
            <Link to="/login">
              <Button className="bg-[#FFD700] text-black hover:bg-[#E5C100] font-bold">
                Go to Main Site
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!storeInfo) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" />
      </div>
    );
  }

  const primaryColor = storeInfo.primary_color || '#FFD700';
  const secondaryColor = storeInfo.secondary_color || '#0A0A0A';

  return (
    <div className="min-h-screen" style={{ backgroundColor: secondaryColor }}>
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-50" style={{ backgroundColor: secondaryColor }}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {storeInfo.logo_url ? (
              <img src={storeInfo.logo_url} alt={storeInfo.business_name} className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                <Store className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
            )}
            <h1 className="text-xl font-bold text-white font-['Outfit']">{storeInfo.business_name}</h1>
          </div>
          <Link to="/login">
            <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 font-bold">
              Login to Buy
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Welcome Message */}
        {storeInfo.welcome_message && (
          <div className="rounded-xl p-6" style={{ 
            background: `linear-gradient(to right, ${primaryColor}20, transparent)`,
            border: `1px solid ${primaryColor}30`
          }}>
            <p className="text-white">{storeInfo.welcome_message}</p>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Search products..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-10 bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600"
            style={{ borderColor: keyword ? primaryColor : undefined }}
            data-testid="store-search-input"
          />
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={activeCategory === null ? 'default' : 'outline'}
            onClick={() => setActiveCategory(null)}
            style={activeCategory === null ? { 
              backgroundColor: primaryColor, 
              color: 'black' 
            } : { 
              borderColor: `${primaryColor}50`,
              color: primaryColor
            }}
            className="font-bold uppercase tracking-wide"
            data-testid="store-category-all-btn"
          >
            All Products
          </Button>
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={activeCategory === category.id ? 'default' : 'outline'}
              onClick={() => setActiveCategory(category.id)}
              style={activeCategory === category.id ? { 
                backgroundColor: primaryColor, 
                color: 'black' 
              } : { 
                borderColor: `${primaryColor}50`,
                color: primaryColor
              }}
              className="font-bold uppercase tracking-wide"
              data-testid={`store-category-${category.id}-btn`}
            >
              {category.name}
            </Button>
          ))}
        </div>

        {/* Products Grid - 4 per row */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="bg-[#0A0A0A] border-white/10">
                <Skeleton className="h-28 w-full rounded-t-lg bg-white/5" />
                <CardContent className="p-3 space-y-2">
                  <Skeleton className="h-4 w-3/4 bg-white/5" />
                  <Skeleton className="h-3 w-1/2 bg-white/5" />
                  <Skeleton className="h-7 w-full bg-white/5" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No products found</h3>
            <p className="text-zinc-400">Try adjusting your search or category filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredProducts.map((product) => (
              <Card 
                key={product.id} 
                className="bg-[#0A0A0A] border-2 hover:shadow-lg transition-all duration-300 overflow-hidden h-full"
                style={{ 
                  borderColor: `${primaryColor}20`,
                  ['--hover-border-color']: primaryColor 
                }}
                data-testid={`store-product-card-${product.id}`}
              >
                <div className="relative h-28 bg-gradient-to-br from-[#121212] to-[#0A0A0A] overflow-hidden">
                  <ProductImage 
                    src={product.thumbnail} 
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                  {product.category_name && (
                    <Badge 
                      className="absolute top-1.5 left-1.5 text-xs font-bold px-1.5 py-0.5 backdrop-blur-sm z-10"
                      style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: primaryColor }}
                    >
                      {product.category_name}
                    </Badge>
                  )}
                </div>
                <CardContent className="p-3 space-y-2">
                  <h3 className="font-bold text-white text-sm line-clamp-1">
                    {product.title}
                  </h3>
                  
                  <div className="flex items-center gap-1.5">
                    <span className="text-base font-black" style={{ color: primaryColor }}>
                      {formatMoney(product.price)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-zinc-500">
                      <span className={`font-bold ${product.stock > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {product.stock}
                      </span> in stock
                    </span>
                    <Link to="/login">
                      <Button
                        size="sm"
                        disabled={product.stock === 0}
                        style={{ backgroundColor: primaryColor, color: 'black' }}
                        className="h-7 px-2 text-xs font-bold hover:opacity-90"
                        data-testid={`store-buy-${product.id}`}
                      >
                        <ShoppingCart className="w-4 h-4 mr-1" />
                        Buy
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-zinc-500 text-sm">
            Powered by <span className="font-bold text-[#FFD700]">CrackBox</span>
          </p>
        </div>
      </footer>
    </div>
  );
}
